import { GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { demeterPositionsApi } from '../../../../../Apis/Apis';
import AgGridBuilder from '../../../../../Components/AgGridBuilder/AgGridBuilder';
import applicationConstants from '../../../../../Core/Utility/ApplicationConstants';
import { DemeterCommodity, DemeterInsurancePositionModel, ListDemeterInsurancePositionsResponse, SortDirection } from '../../../../../Generated/Raven-Demeter';
import useApiWithoutAutoExecute from '../../../../Apis/Hooks/useApiWithoutAutoExecute';
import useSymbolsApi from '../../../../Apis/Hooks/useSymbolsApiHook';
import CheckboxDropdown from '../../../../Components/Form/Inputs/CheckboxDropdown';
import ComponentHeaderRightSideActions, { ComponentHeaderRightSideOptions } from '../../../../Components/Form/Inputs/ComponentMoreOptionsHorizontalDots';
import { ApiListResponse } from '../../../../Components/Tables/TableWrapper/ApiListResponse';
import PaginationTableWrapper from '../../../../Components/Tables/TableWrapper/PaginationTableWrapper';
import languageService from '../../../../Services/Language/LanguageService';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import styles from './MarginPositions.module.scss';
import { marginPositionsColumnDefinitions, marginPositionsColumnOptions } from './MarginPositionsDefinitions';

interface ListInsurancePositionsByCommodityRequest {
    commodity: string;
    sortColumn?: string;
    sortDirection?: SortDirection;
    take: number;
    skip: number;
}

interface IMarginPositionsTable {
    isCalculatorLoading: boolean;
    selectedCalculatorCommodity?: DemeterCommodity;
}

const MarginPositionsTable: React.FC<IMarginPositionsTable> = (props) => {
    const [translations, translate] = useLanguage();
    const [onGridReady, setOnGridReady] = useState<GridReadyEvent>();
    const gridReference = useRef<AgGridReact>(null);
    const symbols = useSymbolsApi();
    const [listMarginPositions, setListMarginPositions] = useState<ListDemeterInsurancePositionsResponse | null>(null);
    const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
    const [selectedSubCommodities, setSelectedSubCommodities] = useState<DemeterCommodity[]>([]);
    const [currentSkip, setCurrentSkip] = useState<number>(0);
    const [currentTake, setCurrentTake] = useState<number>(applicationConstants.ItemsPerPage);
    const [listInsurancePositionsCommodityRequest, setListInsurancePositionsCommodityRequest] = useState<ListInsurancePositionsByCommodityRequest>({
        commodity: props.selectedCalculatorCommodity ?? DemeterCommodity.Milk,
        sortColumn: 'salesDate',
        sortDirection: SortDirection.Asc,
        take: -1,
        skip: 0,
    });

    const [isListInsurancePositionsByCommodityLoading, refreshListInsurancePositionsByCommodity, listInsurancePositionsByCommodityResponse] =
        useApiWithoutAutoExecute(() => {
            if (!props.selectedCalculatorCommodity || !listInsurancePositionsCommodityRequest) {
                return null;
            }

            return demeterPositionsApi.listInsurancePositionsByCommodity(
                listInsurancePositionsCommodityRequest.commodity,
                listInsurancePositionsCommodityRequest.sortColumn !== 'coveragePeriod' ? listInsurancePositionsCommodityRequest.sortColumn : 'salesDate',
                listInsurancePositionsCommodityRequest.sortDirection,
                -1,
                0,
            );
        });

    const entityOptions = useMemo(() => {
        if (!listInsurancePositionsByCommodityResponse?.rows) {
            return [];
        }

        const uniqueEntities = Array.from(new Set(listInsurancePositionsByCommodityResponse.rows.map((row) => row.companyName)));
        return uniqueEntities.map((entity) => ({ label: entity, value: entity }));
    }, [listInsurancePositionsByCommodityResponse]);

    const subCommodityOptions = useMemo(() => {
        if (!listInsurancePositionsByCommodityResponse?.rows) {
            return [];
        }

        const filteredRows = listInsurancePositionsByCommodityResponse.rows;
        const uniqueSubCommodities = Array.from(new Set(filteredRows.map((row) => row.subCommodity).filter(Boolean))) as DemeterCommodity[];

        return uniqueSubCommodities.map((subCommodity) => ({
            label: translations.commodity[subCommodity] || translate(subCommodity),
            value: subCommodity,
        }));
    }, [listInsurancePositionsByCommodityResponse, translations]);

    const filteredPositions = useMemo(() => {
        if (!listMarginPositions?.rows) {
            return [];
        }

        return listMarginPositions.rows.filter((position) => {
            if (selectedEntities.length > 0 && !selectedEntities.includes(position.companyName)) {
                return false;
            }

            if (selectedSubCommodities.length > 0) {
                if (!position.subCommodity || !selectedSubCommodities.includes(position.subCommodity)) {
                    return false;
                }
            }

            return true;
        });
    }, [listMarginPositions, selectedEntities, selectedSubCommodities]);

    const sortedPositions = useMemo(() => {
        if (!filteredPositions || filteredPositions.length === 0) {
            return [];
        }

        const sorted = [...filteredPositions].sort((a, b) => {
            let aValue: number | string | undefined | null;
            let bValue: number | string | undefined | null;

            if (listInsurancePositionsCommodityRequest.sortColumn === 'coveragePeriod') {
                aValue = a.coveragePeriodDate || a.coverageStartDate;
                bValue = b.coveragePeriodDate || b.coverageStartDate;
            } else {
                aValue = a[listInsurancePositionsCommodityRequest.sortColumn as keyof DemeterInsurancePositionModel];
                bValue = b[listInsurancePositionsCommodityRequest.sortColumn as keyof DemeterInsurancePositionModel];
            }

            if (aValue === null || aValue === undefined) {
                return 1;
            }

            if (bValue === null || bValue === undefined) {
                return -1;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return listInsurancePositionsCommodityRequest.sortDirection === SortDirection.Asc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            if (aValue < bValue) {
                return listInsurancePositionsCommodityRequest.sortDirection === SortDirection.Asc ? -1 : 1;
            }

            if (aValue > bValue) {
                return listInsurancePositionsCommodityRequest.sortDirection === SortDirection.Asc ? 1 : -1;
            }

            return 0;
        });

        return sorted;
    }, [filteredPositions, listInsurancePositionsCommodityRequest.sortColumn, listInsurancePositionsCommodityRequest.sortDirection]);

    const paginatedPositions = useMemo(() => {
        if (!sortedPositions || sortedPositions.length === 0) {
            return [];
        }

        const startIndex = currentSkip;
        const endIndex = startIndex + currentTake;
        return sortedPositions.slice(startIndex, endIndex);
    }, [sortedPositions, currentSkip, currentTake]);

    const paginatedApiResponse = useMemo(() => {
        if (!listMarginPositions) {
            return undefined;
        }

        return {
            ...listMarginPositions,
            rows: paginatedPositions,
            totalCount: sortedPositions.length,
            skip: currentSkip,
            take: currentTake,
        };
    }, [listMarginPositions, paginatedPositions, sortedPositions, currentSkip, currentTake]);

    useEffect(() => {
        setSelectedEntities([]);
        setSelectedSubCommodities([]);
        setCurrentSkip(0);
    }, [props.selectedCalculatorCommodity]);

    useEffect(() => {
        if (!symbols || !marginPositionsColumnDefinitions.length) {
            return;
        }

        const commodityColumn = marginPositionsColumnDefinitions.find((x) => x.colId === 'commodity');
        if (commodityColumn) {
            commodityColumn.valueFormatter = (parameters: ValueFormatterParams) => {
                const symbolMatch = symbols.find((x) => x.commodity === parameters.value);
                return symbolMatch ? languageService.toDisplayName(symbolMatch) : translate(parameters.value);
            };
        }

        const subCommodityColumn = marginPositionsColumnDefinitions.find((x) => x.colId === 'subCommodity');
        if (subCommodityColumn) {
            subCommodityColumn.valueFormatter = (parameters: ValueFormatterParams) => {
                if (!parameters.value) {
                    return applicationConstants.TablePlaceholderZeroOrEmpty;
                }
                return translations.commodity[parameters.value as DemeterCommodity] || translate(parameters.value);
            };
        }

        const declaredProductionColumn = marginPositionsColumnDefinitions.find((x) => x.colId === 'declaredProduction');
        const protectionFactorColumn = marginPositionsColumnDefinitions.find((x) => x.colId === 'protectionFactor');
        const protectedProductionColumn = marginPositionsColumnDefinitions.find((x) => x.colId === 'protectedProduction');
        const settlementDateColumn = marginPositionsColumnDefinitions.find((x) => x.colId === 'settlementDate');
        if (
            props.selectedCalculatorCommodity !== DemeterCommodity.Milk &&
            props.selectedCalculatorCommodity !== DemeterCommodity.MilkClass3 &&
            props.selectedCalculatorCommodity !== DemeterCommodity.MilkClass4
        ) {
            declaredProductionColumn!.hide = true;
            protectionFactorColumn!.hide = true;
            protectedProductionColumn!.hide = true;
            settlementDateColumn!.hide = true;
        } else {
            declaredProductionColumn!.hide = false;
            protectionFactorColumn!.hide = false;
            protectedProductionColumn!.hide = false;
            settlementDateColumn!.hide = false;
        }
    }, [symbols, marginPositionsColumnDefinitions, props.selectedCalculatorCommodity]);

    useEffect(() => {
        if (!listInsurancePositionsByCommodityResponse?.rows || !symbols || props.isCalculatorLoading) {
            return;
        }

        setListMarginPositions({
            ...listInsurancePositionsByCommodityResponse,
            rows: listInsurancePositionsByCommodityResponse.rows.map((row: DemeterInsurancePositionModel, index) => ({
                ...row,
                id: `${props.selectedCalculatorCommodity}_${index}`,
            })),
        });
    }, [listInsurancePositionsByCommodityResponse, symbols, props.isCalculatorLoading]);

    useEffect(() => {
        if (!gridReference?.current?.api || !onGridReady) {
            return;
        }

        gridReference.current.api.addEventListener('sortChanged', handleSort);
    }, [gridReference?.current?.api, onGridReady]);

    useEffect(
        () => () => {
            if (gridReference?.current?.api) {
                gridReference.current.api.removeEventListener('sortChanged', handleSort);
            }
        },
        [],
    );

    useEffect(() => {
        if (!props.selectedCalculatorCommodity || props.isCalculatorLoading || isListInsurancePositionsByCommodityLoading) {
            return;
        }

        setListInsurancePositionsCommodityRequest((previous) => ({
            ...previous,
            commodity: props.selectedCalculatorCommodity as string,
            sortColumn: 'salesDate',
            sortDirection: SortDirection.Asc,
        }));

        setListMarginPositions(null);
        refreshListInsurancePositionsByCommodity();
    }, [props.selectedCalculatorCommodity, props.isCalculatorLoading]);

    const handleSort = () => {
        const columnState = gridReference?.current?.columnApi.getColumnState();
        const columnsWithActiveSort = columnState?.filter((x) => x.sort !== null && x.sortIndex !== null);
        if (!columnsWithActiveSort?.length) {
            return;
        }

        const newSortColumn = columnsWithActiveSort[0].colId;
        const newSortDirection = columnsWithActiveSort[0].sort === SortDirection.Asc.toLowerCase() ? SortDirection.Asc : SortDirection.Desc;

        setListInsurancePositionsCommodityRequest((previous) => ({
            ...previous,
            sortColumn: newSortColumn,
            sortDirection: newSortDirection,
        }));
        setCurrentSkip(0);
    };

    const handleTakeCountUpdate = (apiListResponse: ApiListResponse) => {
        setCurrentTake(apiListResponse.take ?? applicationConstants.ItemsPerPage);
        setCurrentSkip(0);
    };

    const handlePagination = (apiListResponse: ApiListResponse) => {
        setCurrentSkip(apiListResponse.skip ?? 0);
    };

    const handleMoreOptionsSelection = (value: ComponentHeaderRightSideOptions) => {
        if (!gridReference?.current?.api) {
            return;
        }

        gridReference.current.api.setRowData(listMarginPositions?.rows || []);

        if (value === ComponentHeaderRightSideOptions.Xlsx) {
            gridReference.current.api.exportDataAsExcel({
                fileName: 'MarginPositions.xlsx',
            });
        }

        if (value === ComponentHeaderRightSideOptions.Csv) {
            gridReference.current.api.exportDataAsCsv({
                fileName: 'MarginPositions.csv',
            });
        }

        gridReference.current.api.setRowData(paginatedPositions);
    };

    return (
        <div className={styles.margin_positions_table_container}>
            <div className={styles.margin_positions_actions}>
                <div className={styles.margin_positions_filters}>
                    <div className={styles.margin_positions_dropdown}>
                        <CheckboxDropdown
                            label={translations.positions.fields.companyName}
                            labelType="selectedTextValues"
                            placeholder={!selectedEntities.length ? translations.words.select : ''}
                            values={selectedEntities}
                            options={entityOptions}
                            handleOptionSelect={(value) => {
                                setSelectedEntities(value);
                                setCurrentSkip(0);
                            }}
                            testId="EntityFilter"
                            track={{ eventCategory: 'MarginCalculator', eventAction: 'PositionsCompanyChanged' }}
                        />
                    </div>
                    {subCommodityOptions.length !== 0 && (
                        <div className={styles.margin_positions_dropdown}>
                            <CheckboxDropdown
                                label={translations.positions.fields.subCommodity}
                                labelType="selectedTextValues"
                                placeholder={!selectedSubCommodities.length ? translations.words.select : ''}
                                values={selectedSubCommodities}
                                options={subCommodityOptions}
                                handleOptionSelect={(value) => {
                                    setSelectedSubCommodities(value);
                                    setCurrentSkip(0);
                                }}
                                testId="SubCommoditiesFilter"
                                track={{
                                    eventCategory: 'MarginCalculator',
                                    eventAction: 'PositionsSubCommoditiesChanged',
                                    eventValue: selectedSubCommodities,
                                }}
                            />
                        </div>
                    )}
                </div>
                <ComponentHeaderRightSideActions
                    name="MarginPositionsDownloadButton"
                    handleMoreOptionsSelection={handleMoreOptionsSelection}
                    isCsvDownloadAvailable
                    isXlsxDownloadAvailable
                    ignoreDefaultDownloadOptions
                    hidePopoutModal
                    testId="MarginPositionsDownloadButton"
                    track={{ eventCategory: 'MarginCalculator', eventAction: 'MarginPositionsDownloadClicked' }}
                />
            </div>
            <PaginationTableWrapper
                apiListResponse={paginatedApiResponse}
                showPagination
                handlePagination={handlePagination}
                handleTakeCountUpdate={handleTakeCountUpdate}
                track={{ eventCategory: 'MarginCalculator', eventAction: 'MarginPositionsPaginationChanged' }}
            >
                <AgGridBuilder
                    gridRef={gridReference}
                    rowData={paginatedPositions}
                    onGridReady={setOnGridReady}
                    columnDefinitions={marginPositionsColumnDefinitions}
                    defaultColumnDefinition={marginPositionsColumnOptions}
                    gridHeightFull
                    domLayout="autoHeight"
                    testId="PositionsTable"
                />
            </PaginationTableWrapper>
        </div>
    );
};

export default MarginPositionsTable;
